<template>
  <frontoffice-frame>
    <b-row v-if="isLoading" class="loader-section">
      <frontoffice-loader />
    </b-row>

    <div v-else-if="!serverError" class="booking-summary">
      <b-row>
        <b-col cols="12 p-0">
          <page-title>
            {{ $t('booking.summary.title') }}
          </page-title>
        </b-col>
      </b-row>

      <b-row align-h="center">
        <b-col cols="12"  class="summary-wrapper">
          <b-row class="sum-item-row">
            <b-col cols="12" lg="6" class="sum-item-col">
              <summary-item :title="$t('_journey_dates')" fa-class="calendar-alt">
                {{ $t('_period.from') }} {{ formatDate(offer.from.date) }}
                <br>
                {{ $t('_period.to') }} {{ formatDate(offer.to.date) }}
              </summary-item>
            </b-col>

            <b-col sm="12" lg="6" class="sum-item-col">
              <summary-item :title="$t('booking.summary.place')" fa-class="map-marker">
                <div class="hotel-name">
                  {{ offer.place.hotelName }}
                </div>

                <div class="hotel-address">
                  <div v-if="offer.place.address.address1">
                    {{ offer.place.address.address1 }}
                  </div>

                  <div v-if="offer.place.address.address2">
                    {{ offer.place.address.address2 }}
                  </div>

                  <div v-if="offer.place.address.postCode || offer.place.address.city">
                    {{ offer.place.address.postCode }} {{ offer.place.address.city }}
                  </div>
                </div>
              </summary-item>
            </b-col>
          </b-row>

          <hr>

          <b-row class="sum-item-row">
            <b-col sm="12" lg="6" class="sum-item-col">
              <summary-item :title="travellers.length + ' ' + $tc('booking.summary.traveller_s', travellers.length)"
                            fa-class="user-friends"
              >
                <span class="traveller">
                  {{ allTravellers }}
                </span>
              </summary-item>
            </b-col>

            <b-col sm="12" lg="6" class="sum-item-col">
              <summary-item :title="nbRooms + ' ' + $tc('booking.summary.room_s', nbRooms)"
                            fa-class="bed"
              >
                <span v-if="nbSingleRooms" class="single">
                  {{ nbSingleRooms }}
                  {{ $tc('online.booking.summary.room.single', nbSingleRooms) }}
                </span>

                <span v-if="nbDoubleRooms" class="double">
                  {{ nbDoubleRooms }}
                  {{ $tc('online.booking.summary.room.double', nbDoubleRooms) }}
                </span>
              </summary-item>
            </b-col>
          </b-row>

          <hr>

          <b-row class="sum-item-row">
            <div class="col-12 col-lg-6 sum-item-col price-nbNight">
              <summary-item :title="$t('_price')" fa-class="euro-sign">
                <span class="title">
                  {{ offer.nbNights + ' ' + $tc('booking.summary.night_s', offer.nbNights) }}
                </span>
              </summary-item>

              <prices :booking="booking" />

              <div class="cancellation" :class="cancellationDetails.class">
                {{ cancellationDetails.text }}
              </div>

              <div>
                {{ booking.cancellation_conditions }}
              </div>
            </div>

            <b-col sm="12" lg="6" v-if="offer.customFields.length" class="sum-item-col">
              <summary-item :title="$t('booking.summary.refs')" fa-class="info">
                <div v-for="ref in offer.customFields">
                  <span class="ref-title">
                    {{ ref.label }} :
                  </span>

                  <span class="value">
                    {{ ref.value }}
                  </span>
                </div>
              </summary-item>
            </b-col>

            <b-col sm="12" lg="6" v-if="offer.comment" class="sum-item-col">
              <summary-item :title="$t('booking.summary.comment')" fa-class="sticky-note">
                <p class="offer-comment">{{ offer.comment }}</p>
              </summary-item>
            </b-col>
            <div v-if="booking.managers && allManagers" class="col-12 col-lg-6 sum-item-col">
              <summary-item fa-class="user-check"
                            :title="$tc('booking.managersValidation.summaryItemTitle', booking.managers.length)"
              >
                {{ allManagers }}
              </summary-item>
            </div>
          </b-row>

          <hr/>

          <div v-if="isCreditCardRequired">
              <summary-item title="Garantie carte bancaire" fa-class="credit-card">
                  <b-alert :show="paymentError" variant="danger">{{ $t('error.' + this.paymentErrorDetails) }}</b-alert>
                  <b-alert show variant="warning" >{{ $t('booking.summary.paymentCB.message.' + this.financialWarrantyMode) }}</b-alert>
                  <form ref="formCB">
                    <ticPaymentMethodCB :value="creditCard" />
                  </form>
              </summary-item>
          </div>
          <div v-else-if="paymentError">
            <b-alert :show="paymentError" variant="danger">{{ $t('error.' + this.paymentErrorDetails) }}</b-alert>
          </div>

        </b-col>
      </b-row>

      <b-row class="summary-buttons">
        <b-col cols="12" lg="4" class="text-lg-left">
          <button class="btn orange-white" v-b-modal="'booking-cancel'">
            {{ $t('_cancel') }}
          </button>

          <b-modal id="booking-cancel" :cancel-title="$t('_no')"
                   :ok-title="$t('_yes')" @ok="handleCancellationConfirm"
          >
            {{ $t('online.cancel.question') }}
          </b-modal>
        </b-col>

        <b-col cols="12" lg="4" class="text-lg-center">
          <button class="btn orange-white" @click="onModify">
            {{ $t('_modify') }}
          </button>
        </b-col>

        <b-col cols="12" lg="4" class="text-lg-right">
          <button class="btn orange-white" @click="onConfirm" v-if="isCreditCardRequired">
            {{ $t('booking.summary.btn_confirm_and_pay') }}
          </button>
          <button class="btn orange-white" @click="onConfirm" v-else>
            {{ $t('booking.summary.btn_confirm') }}
          </button>
        </b-col>
      </b-row>
    </div>

    <error-modal v-if="booking" :booking-id="booking.id" />
  </frontoffice-frame>
</template>

<script>
import frontofficeLoader from '@/components/frontoffice/loader'
import formatter from '@/components/global/mixins/formatterMethods'
import frontofficeFrame from '@/components/frontoffice/frame'
import pageTitle from '@/components/frontoffice/pageTitle'
import summaryItem from '@/components/frontoffice/OnlineBooking/summaryItem'
import radioButton from '@/components/frontoffice/form/baseInput/radio'
import FormSubmitter from '@/api/formSubmitter'
import CommonApi from '@/api/commonApi'
import prices from '@/components/frontoffice/Booking/prices'
import bookingDeadlineMixin from '@/components/frontoffice/mixins/bookingDeadline'
import { fieldTypes } from '@/utils/fieldTypeCodes'
import errorModal from '@/components/frontoffice/Booking/modal/onlineConfirmError'
import textInput from '@/components/frontoffice/form/customInput/text'
import selectInput from '@/components/frontoffice/form/customInput/select'
import ticPaymentMethodCB from '@/components/frontoffice/form/paymentMethodCB.vue'

export default {
  components: {
    frontofficeLoader,
    frontofficeFrame,
    pageTitle,
    summaryItem,
    radioButton,
    prices,
    errorModal,
    textInput,
    selectInput,
    ticPaymentMethodCB
  },

  mixins: [formatter, bookingDeadlineMixin],

  data () {
    return {
      commonApi: new CommonApi(),
      isLoading: true,
      serverError: false,
      paymentError: false,
      paymentErrorDetails: '',
      travellers: [],
      offer: null,
      booking: null,
      isCreditCardRequired: false,
      cancellationDetails: null,
      creditCard: { organisation: '', cardHolder: '', number: '', securityCode: '', valid: '' },
      financialWarrantyMode: null
    }
  },

  created () {
    this.commonApi.getUnfinishedOnlineBooking(this.$store.state.onlineBooking.unfinishedId)
      .then(response => {
        this.offer = response.data.data.cache
        this.booking = response.data.data.booking
        this.isCreditCardRequired = response.data.data.isCreditCardRequired
        this.financialWarrantyMode = response.data.data.financialWarrantyMode
      })
      .then(() => { this.setCancellationDetails() })
      .then(() => { this.translateCustomFields() })
      .then(() => { this.isLoading = false })
  },

  methods: {
    onConfirm () {
      if (!this.$refs.hasOwnProperty('formCB') || this.$refs.formCB.reportValidity()) {
        let submitter = new FormSubmitter('online_validate')
        this.isLoading = true
        this.paymentError = false
        this.paymentErrorDetails = ''
        submitter.submitRequest({
          bookingId: this.offer.bookingId,
          hotelKey: this.$store.state.onlineBooking.hotelKey,
          offer: this.offer,
          selectedRooms: this.$store.state.onlineBooking.selectedRooms,
          creditCard: this.creditCard
        })
          .then(response => {
            if (response['data']['success']) {
              this.dealSuccess(
                response['data']['data']['newId'],
                response['data']['data']['requireValidation']
              )
            } else {
              // payment => error
              if (response['data']['data']['paymentError']) this.dealPaymentFailure(response['data']['errors'])
              else this.dealFailure()
            }
          }).catch((error) => {
            console.log(error)
            this.dealFailure()
          })
      }
    },

    onModify () {
      let submitter = new FormSubmitter('delete_unfinished_booking')

      submitter.submitRequest(null, this.offer.bookingId)

      this.$router.push({ name: 'online_booking_completion', params: { action: 'edit' } })
    },

    dealSuccess (newId, requireValidation) {
      // Reset search criterias
      this.$store.commit('research/reset')
      this.$store.commit('onlineBooking/resetCurrent')

      this.$router.push({
        name: 'booking_request_confirm',
        params: {
          id: newId,
          online: true,
          requireValidation: requireValidation,
          requireOrderNumber: this.$store.getters['security/company'].orderNumber && this.$store.getters['security/company'].orderNumber.required
        }
      })
    },

    dealFailure () {
      this.isLoading = false
      this.serverError = true
      this.$bvModal.show('online_confirm_error')
    },

    dealPaymentFailure (log) {
      this.isLoading = false
      this.paymentError = true
      this.paymentErrorDetails = log[0]
    },

    handleCancellationConfirm () {
      this.$store.commit('onlineBooking/reset')
      this.$router.push({
        name: 'home_search'
      })
    },

    setCancellationDetails () {
      let rooms = []

      let getRooms = (type) => {
        let rooms = []

        if (this.offer.bedrooms[type].amount) {
          for (let room of this.offer.bedrooms[type].travellers) {
            rooms.push({
              offer: room.roomInfos.offerDetails
            })
          }
        }

        return rooms
      }

      rooms = rooms.concat(getRooms('single'))
      rooms = rooms.concat(getRooms('double'))

      this.cancellationDetails = this.getNearestDeadlineDetails(rooms)
    },

    translateCustomFields () {
      this.offer.customFields.map(field => {
        if (field.type === fieldTypes.BOOLEAN) {
          switch (field.value) {
            case 'yes': field.value = this.$t('_yes'); break
            case 'no': field.value = this.$t('_no'); break
          }
        }
      })
    }
  },

  computed: {
    allTravellers () {
      let ret = []
      let roomValues = Object.values(this.offer.bedrooms)

      for (let i in roomValues) {
        if (roomValues.hasOwnProperty(i)) {
          let roomType = roomValues[i]
          roomType.travellers.forEach(room => {
            for (let key in room) {
              if (room.hasOwnProperty(key)) {
                if (!isNaN(parseInt(key))) {
                  let traveller = room[key]
                  this.travellers.push(traveller)
                  ret.push(this.fullName(traveller))
                }
              }
            }
          })
        }
      }

      return ret.join(', ')
    },

    allManagers () {
      let arr = this.booking.managers.flatMap(manager => this.fullName(manager, false))
      return arr.join(', ')
    },

    nbRooms () {
      return this.nbSingleRooms + this.nbDoubleRooms
    },

    nbSingleRooms () {
      return this.offer.bedrooms.single.amount
    },

    nbDoubleRooms () {
      return this.offer.bedrooms.double.amount
    },

    pricePerRoomPerNight () {
      return this.offer.price.overall / this.offer.nbNights / this.nbRooms
    }
  }
}
</script>
